import { AlarmClock } from "lucide-react";
import type { FC } from "react";

import { cn } from "@/lib/utils";

import {
  daysUntil,
  deadlineUrgency,
  formatDeadline,
  relativeDeadline,
  type CaseDeadline
} from "./caseHeaderModel";
import { RowLabel } from "./chips/chip";

/** Dates shown in the popover, items under one date, and how far past a deadline still shows. */
const DATES_SHOWN = 3;
const ITEMS_PER_DATE = 3;
const PAST_DAYS_SHOWN = 30;
const RESPOND = "Respond to ";

type Urgency = ReturnType<typeof deadlineUrgency>;

const TILE: Record<Urgency, string> = {
  overdue: "bg-danger-soft text-danger",
  soon: "bg-warn-soft text-warn",
  ok: "bg-fill-2 text-fg-2",
  none: "bg-fill-2 text-fg-2"
};

const INK: Record<Urgency, string> = {
  overdue: "text-danger",
  soon: "text-warn",
  ok: "text-fg-3",
  none: "text-fg-3"
};

export type DeadlineGroup = {
  date: string;
  /** "Respond by Oct 28" when every item is a response; else the lone label or the date. */
  title: string;
  /** The lines under the title: the items without "Respond to", or a lone deadline's full date. */
  items: string[];
};

/**
 * Deadlines on the same day collapse into one group, so two notices due
 * together read as one date. Every group is a title over at least one line.
 * Soonest first, as the facts derive them.
 */
export const groupDeadlines = (deadlines: readonly CaseDeadline[]) => {
  const byDay = new Map<string, CaseDeadline[]>();
  deadlines.forEach((d) => {
    const day = formatDeadline(d.date);
    if (!day) return;
    byDay.set(day, [...(byDay.get(day) ?? []), d]);
  });
  return [...byDay.entries()].map(([day, list]): DeadlineGroup => {
    const [{ date }] = list;
    const shortDay = new Date(date).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric"
    });
    if (list.every((d) => d.label.startsWith(RESPOND)))
      return {
        date,
        title: `Respond by ${shortDay}`,
        items: list.map((d) => d.label.slice(RESPOND.length))
      };
    if (list.length === 1) return { date, title: list[0].label, items: [day] };
    return { date, title: day, items: list.map((d) => d.label) };
  });
};

const sentence = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

const Group: FC<{ group: DeadlineGroup }> = ({ group }) => {
  const d = new Date(group.date);
  const urgency = deadlineUrgency(group.date);
  const shown = group.items.slice(0, ITEMS_PER_DATE);
  const more = group.items.length - shown.length;
  return (
    <li className="flex min-w-0 gap-2.5" data-testid="deadline-group">
      <span
        aria-hidden
        className={cn(
          "flex h-9 w-8 shrink-0 flex-col items-center justify-center gap-0.5 rounded-md leading-none",
          TILE[urgency]
        )}
      >
        <span className="text-xs font-medium uppercase">
          {d.toLocaleDateString(undefined, { month: "short" })}
        </span>
        <span className="text-sm font-medium tabular-nums">{d.getDate()}</span>
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-0.5 pt-0.5">
        <span className="flex min-w-0 items-baseline gap-2 text-xs">
          <span className="truncate font-medium text-fg" title={group.title}>
            {group.title}
          </span>
          <span
            className={cn("ml-auto shrink-0", INK[urgency])}
            title={formatDeadline(group.date)}
          >
            {sentence(relativeDeadline(group.date))}
          </span>
        </span>
        {shown.map((item) => (
          <span key={item} className="truncate text-xs text-fg-2" title={item}>
            {item}
          </span>
        ))}
        {more ? <span className="text-xs text-fg-3">+{more} more</span> : null}
      </span>
    </li>
  );
};

/** The details popover's Deadlines section, under its own rule: one calendar tile per date, its parts beneath. */
export const DeadlinesSection: FC<{ deadlines: readonly CaseDeadline[] }> = ({
  deadlines
}) => {
  const groups = groupDeadlines(
    deadlines.filter((d) => (daysUntil(d.date) ?? 0) >= -PAST_DAYS_SHOWN)
  );
  if (!groups.length) return null;
  const shown = groups.slice(0, DATES_SHOWN);
  const hidden = groups.length - shown.length;

  return (
    <section className="flex flex-col" data-testid="case-deadlines">
      <div className="my-1 h-px bg-border" />
      <RowLabel className="h-7 w-auto">
        <AlarmClock className="size-3.5 text-fg-3" /> Deadlines
      </RowLabel>
      <ul className="flex flex-col gap-2">
        {shown.map((g) => (
          <Group key={g.date} group={g} />
        ))}
      </ul>
      {hidden ? (
        <span className="pt-1.5 pl-10.5 text-xs text-fg-3">
          +{hidden} more {hidden === 1 ? "date" : "dates"} in Case Insights
        </span>
      ) : null}
    </section>
  );
};
